import type { ComponentProps } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { rem } from 'polished'
import styled from 'styled-components'

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText' | 'primaryActionTo'>

const TermsMarkdown = styled(MarkdownRender)`
  p {
    margin-bottom: ${rem(12)};
  }

  h3 {
    margin-top: ${rem(4)};
    margin-bottom: ${rem(8)};
    color: ${props => props.theme.palette.neutral.default.one};
    font-size: ${rem(16)};
    line-height: ${rem(22)};
  }

  ul {
    margin-top: 0;
    margin-bottom: ${rem(18)};
    padding-left: ${rem(20)};
  }

  li {
    margin-top: ${rem(8)};
    line-height: ${rem(22)};
  }

  li::marker {
    color: #00cfa2;
  }

  table td {
    width: 33.33%;
    padding-right: ${rem(12)};
  }

  table > tbody:first-child > tr:first-child > td {
    border-top: 0;
  }

  table ul {
    margin-top: 0;
  }

  table li {
    margin-top: ${rem(8)};
  }
`

const AIRLINE_EXCLUSION_PATTERN = /^[A-Z0-9]{2}\s+.+$/

function formatAirlineExclusion(line: string): string {
  const [code, ...airlineParts] = line.trim().split(/\s+/)

  if (!code || !airlineParts.length) {
    return line
  }

  return `${code} - ${airlineParts.join(' ')}`
}

function formatTermsAndConditionsAsMarkdown(termsAndConditions: string): string {
  if (
    termsAndConditions.includes('## ')
    || termsAndConditions.includes('[')
    || termsAndConditions.includes('<table>')
  ) {
    return termsAndConditions.trim()
  }

  const lines = termsAndConditions
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  const blocks: Array<string> = []
  const airlineList: Array<string> = []
  let isCollectingAirlineList = false

  for (const line of lines) {
    if (line === 'Excludes:') {
      blocks.push(line)
      isCollectingAirlineList = true
      continue
    }

    if (isCollectingAirlineList && AIRLINE_EXCLUSION_PATTERN.test(line)) {
      airlineList.push(`- ${formatAirlineExclusion(line)}`)
      continue
    }

    if (isCollectingAirlineList) {
      if (airlineList.length) {
        blocks.push(airlineList.join('\n'))
        airlineList.length = 0
      }
      isCollectingAirlineList = false
    }

    blocks.push(line)
  }

  if (airlineList.length) {
    blocks.push(airlineList.join('\n'))
  }

  return blocks.join('\n\n')
}

interface Props extends InheritedModalProps {
  termsAndConditions?: string
}

function TourTermsAndConditionsModal({
  termsAndConditions,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<undefined>()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  return <Modal
    title={intl.formatMessage({
      id: 'O3Ia38blb31Bw3dRvgy9',
      defaultMessage: 'Terms & conditions',
      description: 'Label for the terms and conditions preview card shown above highlighted inclusions on the tour offer detail page',
    })}
    primaryActionText={intl.formatMessage({
      id: 'SAl2bM9TkmEg2qrZJH6g',
      defaultMessage: 'Close',
      description: 'Button text used to close the tour terms and conditions modal',
    })}
    onPrimaryActionClick={handlePrimaryClick}
  >
    <Group direction="vertical" gap={12}>
      {termsAndConditions
        ? <TermsMarkdown content={formatTermsAndConditionsAsMarkdown(termsAndConditions)} fontSize="medium" type="compact" />
        : null}
    </Group>
  </Modal>
}

export default TourTermsAndConditionsModal
